// ── Workflow → sequencediagram.org-style DSL generator ──────────────────────
//
// Walks a Workflow's task tree and emits a DSL string in the dialect accepted
// by src/features/dashboards/components/ClientSequenceDiagram/dsl-parser.ts.
//
// Verified-supported constructs in that parser (see dsl-parser.ts):
//   - `title <text>`
//   - `participant <id> "<label>" "<sublabel>"`   (id must be whitespace-free)
//   - messages: `<from>-><to>: <label>` (sync), `<from>-->'<to>: <label>` (return),
//     `<from>->><to>: <label>` (async/fire-and-forget)
//   - indented detail lines directly under a message: `GET|POST|PUT|DELETE <url>`
//   - `group <condition>` ... `end` — groups NEST (tracked via a stack).
//   - `self <id>: <label>`
//   - `note <id>: <label>`
//   - `---` spacer
//
// The parser has no `alt`/`else`, so BRANCH tasks render as nested `group` blocks.
//
// If the workflow contains an IDV step, the diagram is prefixed with the Hosted
// OneSDK flow: generate the hosted link, the end user opens the page and does
// OCR / biometrics (derived from the IDV node's attestations), submits, and —
// when "run workflow on submission" is enabled — the workflow runs
// automatically. Non-IDV workflows keep the client-initiated async execute call.

import type { Workflow, Task, StepTask } from './types'
import { isBranchTask, isStepTask } from './types'

const ENTITY_PATH = '{entityId}/serviceprofiles/{serviceName}'

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v ? v : undefined
}

// ── IDV detection + attestation collection (from the workflow JSON) ──
interface AttestationPart {
  parts?: { attestationTypes?: string }[]
}

function collectIdv(tasks: Task[]): { present: boolean; attestations: Set<string> } {
  const attestations = new Set<string>()
  let present = false
  const walk = (ts: Task[]) => {
    for (const t of ts) {
      if (isStepTask(t) && t.configuration.name === 'IDV') {
        present = true
        const pfs = t.configuration.parameters?.partsForSources as AttestationPart[] | undefined
        pfs?.forEach((s) => s.parts?.forEach((p) => p.attestationTypes && attestations.add(p.attestationTypes)))
      } else if (isBranchTask(t)) {
        t.configuration.branches.forEach((b) => walk(b.tasks))
      }
    }
  }
  walk(tasks)
  return { present, attestations }
}

// A sequence diagram is a linear interaction timeline, so instead of expanding
// the full branch/decision tree (which produces a confusing wall of repeated
// "Branch: Else → Monitoring OFF → Decision FAIL" terminals for deep cascades)
// we flatten to the external data-source calls in tree order — matching how
// these flows are drawn for clients — and summarise the decision outcomes once
// at the end. The full branch/decision structure lives in the editor canvas.

const DATA_SOURCE_STEPS = new Set(['MATCHLIST', 'KYC', 'IDV', 'AML', 'KYB', 'FRAUD'])

function dataSourceLabel(step: StepTask): string {
  const p = step.configuration.parameters ?? {}
  switch (step.configuration.name) {
    case 'MATCHLIST':
      return 'MATCHLIST — blocklist check'
    case 'KYC': {
      const provider = str(p.providerName) ?? str(p.provider) ?? 'provider'
      const ruleset = str(p.rulesetName)
      return `KYC — ${provider}${ruleset ? ` (${ruleset})` : ''}`
    }
    case 'IDV': {
      const provider = str(p.providerName) ?? 'provider'
      return `IDV — verify ${provider} result`
    }
    case 'AML': {
      const name = str(p.name) ?? 'screening'
      const provider = str(p.providerName)
      return `AML — ${name}${provider ? ` (${provider})` : ''}`
    }
    case 'KYB': {
      const provider = str(p.providerName)
      return `KYB — business verification${provider ? ` (${provider})` : ''}`
    }
    case 'FRAUD': {
      const provider = str(p.providerName) ?? 'provider'
      return `FRAUD — device & risk signal check (${provider})`
    }
    default:
      return step.configuration.name
  }
}

// DFS collect the external data-source STEP calls in order (skips BRANCH,
// DECISION and TOGGLEMONITORING — those are decision logic, not interactions).
function collectInteractions(tasks: Task[]): StepTask[] {
  const out: StepTask[] = []
  const walk = (ts: Task[]) => {
    for (const t of ts) {
      if (isStepTask(t) && DATA_SOURCE_STEPS.has(t.configuration.name)) out.push(t)
      else if (isBranchTask(t)) t.configuration.branches.forEach((b) => walk(b.tasks))
    }
  }
  walk(tasks)
  return out
}

// DFS collect the distinct terminal outcomes (DECISION results) and whether
// monitoring is toggled on/off, so they can be summarised in a single note.
function collectOutcomes(tasks: Task[]): { results: string[]; monitoringOn: boolean; monitoringOff: boolean } {
  const results = new Set<string>()
  let monitoringOn = false
  let monitoringOff = false
  const walk = (ts: Task[]) => {
    for (const t of ts) {
      if (isStepTask(t)) {
        const p = t.configuration.parameters ?? {}
        if (t.configuration.name === 'DECISION') {
          const r = str(p.workflowExecutionResult)
          if (r) results.add(r)
        } else if (t.configuration.name === 'TOGGLEMONITORING') {
          const a = str(p.action)?.toUpperCase()
          if (a === 'ENABLED') monitoringOn = true
          if (a === 'DISABLED') monitoringOff = true
        }
      } else if (isBranchTask(t)) {
        t.configuration.branches.forEach((b) => walk(b.tasks))
      }
    }
  }
  walk(tasks)
  return { results: [...results], monitoringOn, monitoringOff }
}

function emitWorkflowBody(wf: Workflow, lines: string[]): void {
  const interactions = collectInteractions(wf.tasks)
  const kycCount = interactions.filter((s) => s.configuration.name === 'KYC').length

  lines.push(`group Workflow: ${wf.name}`)
  if (interactions.length === 0) {
    lines.push('  note frankieOne: No external checks in this workflow')
  }
  let cascadeNoted = false
  for (const step of interactions) {
    if (!cascadeNoted && kycCount > 1 && step.configuration.name === 'KYC') {
      lines.push('  note frankieOne: KYC cascade — first provider to match passes; otherwise the next is tried')
      cascadeNoted = true
    }
    lines.push(`  frankieOne->dataSources: ${dataSourceLabel(step)}`)
    lines.push('  dataSources-->frankieOne: result')
  }
  lines.push('end')

  const { results, monitoringOn, monitoringOff } = collectOutcomes(wf.tasks)
  if (results.length > 0) {
    const monitoring =
      monitoringOn && monitoringOff
        ? ' — monitoring ON on pass, OFF on fail'
        : monitoringOn
          ? ' — monitoring ON on pass'
          : monitoringOff
            ? ' — monitoring OFF on fail'
            : ''
    lines.push(`note frankieOne: Possible outcomes: ${results.join(' · ')}${monitoring}`)
  }
  lines.push('self frankieOne: Return workflow result')
}

export function workflowToDsl(wf: Workflow): string {
  const lines: string[] = []
  const { present: idv, attestations } = collectIdv(wf.tasks)

  lines.push(`title ${wf.name} — ${idv ? 'Hosted IDV + Workflow' : 'Async Execution Flow'}`)
  lines.push('')
  if (idv) lines.push('participant endUser "End User" "Applicant"')
  lines.push('participant clientBe "Client Backend" "Backend Server"')
  lines.push('participant frankieOne "FrankieOne" "API / Platform"')
  lines.push('participant dataSources "Data Sources" "Third Party"')
  lines.push('')

  if (idv) {
    // Hosted OneSDK IDV happens before the workflow runs.
    lines.push('clientBe->frankieOne: Generate hosted OneSDK verification link')
    lines.push(`  POST /v2/individuals/${ENTITY_PATH}/idv/hosted-url`)
    lines.push('frankieOne-->clientBe: Hosted verification URL (SMS/link sent)')
    lines.push('clientBe->endUser: Send verification link')
    lines.push('endUser->frankieOne: Open hosted OneSDK page')
    lines.push('')

    const wantsOcr = attestations.has('DOCUMENT') || attestations.has('OCR_COMPARISON')
    const wantsBio = attestations.has('FACIAL_COMPARISON') || attestations.has('FACIAL_LIVENESS')
    lines.push('group Hosted OneSDK — IDV capture')
    if (wantsOcr || (!wantsOcr && !wantsBio)) lines.push('  endUser->frankieOne: Capture document (OCR)')
    if (wantsBio) lines.push('  endUser->frankieOne: Selfie + liveness (Biometrics)')
    lines.push('  endUser->frankieOne: Submit verification')
    lines.push('end')
    lines.push('')

    lines.push('note frankieOne: Run workflow on submission (configurable)')
    lines.push(`self frankieOne: Trigger workflow execution — ${wf.name}`)
    lines.push('')
  } else {
    // Client-initiated async execute — diagram starts here; entity creation is out of scope.
    lines.push('clientBe->frankieOne: Execute workflow (async)')
    lines.push(`  POST /v2/individuals/${ENTITY_PATH}/workflows/${wf.name}/execute`)
    lines.push('frankieOne-->clientBe: 202 Accepted { workflowExecutionId }')
    lines.push('')
  }

  // Background execution — flattened external calls + summarised outcomes.
  emitWorkflowBody(wf, lines)
  lines.push('')

  // Webhook (fire-and-forget) + client fetching results.
  const webhookUrl = wf.webhook?.url || '{{client_webhook_url}}'
  lines.push('frankieOne->>clientBe: Webhook — workflow execution complete')
  lines.push(`  POST ${webhookUrl}`)
  lines.push('clientBe->frankieOne: Get workflow execution result')
  lines.push(`  GET /v2/individuals/${ENTITY_PATH}/workflows/${wf.name}/executions/{workflowExecutionId}`)
  lines.push('frankieOne-->clientBe: 200 OK { workflowResult }')
  lines.push('self clientBe: Evaluate workflowResult (PASS / FAIL / REFER)')

  return lines.join('\n')
}
